<template>
  <div>
    <vue-autosuggest
      :id="[type,'search']"
      ref="autocompleteInput"
      :suggestions="suggestions"
      :input-props="inputProps"
      :section-configs="sectionConfigs"
      :get-suggestion-value="getSuggestionValue"
      :on-selected="submit"
      aria-label="search"
    >
      <template slot-scope="{suggestion}">
        <span v-html="renderSuggestion(suggestion)" />
      </template>
    </vue-autosuggest>

    <div
      v-if="autocompleteText.length && !ui.loading"
      class="ResetInput"
      @click="resetText"
    >
      <span class="far fa-times" />
    </div>

    <button
      :class="['Button Button--transparentBg',{'Button--primary': autocompleteText !== '', 'is-loading': ui.loading}]"
      aria-label="Search"
      @click="submit({ text: autocompleteText })"
    >
      <span class="far fa-search" />
    </button>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { VueAutosuggest } from 'vue-autosuggest';
import axios from 'axios';
import _ from '@/lodash';

export default {
  components: {
    'vue-autosuggest': VueAutosuggest,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      ui: {
        loading: false,
      },
      cancellationTokens: [],
      config: {
        suggestionsUrl: '/suggestions.php',
      },
      autocompleteText: '',
      inputProps: {
        id: this.type,
        onInputChange: this.handler,
        placeholder: 'Start your search...',
        initialValue: '',
        class: 'form-control',
        name: 's',
      },
      suggestions: [],
      sectionConfigs: {
        jobRoles: {
          limit: 10,
          label: 'Roles',
          onSelected: (selected, originalInput) => {
            this.submit({ text: originalInput });
          },
        },
        companyIndustries: {
          limit: 10,
          label: 'Industries',
          onSelected: (selected, originalInput) => {
            this.submit({ text: originalInput });
          },
        },
        companies: {
          limit: 10,
          label: 'Companies',
          onSelected: (selected, originalInput) => {
            this.submit({ text: originalInput });
          },
        },
        default: {
          onSelected: (item, originalInput) => {
            this.submit({ text: originalInput });
          },
        },
      },
    };
  },

  computed: {
    ...mapState('search', {
      textQuery: (state) => state.query.s,
      isFirstLoading: (state) => state.isFirstLoading,
      searchType: (state) => state.query.show,
    }),
  },

  watch: {
    textQuery() {
      if (this.textQuery === '') {
        this.resetText();
      } else {
        this.$refs.autocompleteInput.searchInput = this.textQuery;
      }
    },
  },

  // Emitted from resources/assets/vue/vanilla/search/mobile-search.js
  // this is the bridge between vanilla js and vue because of
  // Googles LCP performance penalties
  created() {
    this.$bus.$on('bus:mobile-header:search', this.submit);
  },
  beforeDestroy() {
    this.$bus.$off('bus:mobile-header:search', this.submit);
  },

  beforeMount() {
    this.inputProps.initialValue = this.textQuery;
  },

  methods: {
    ...mapMutations('search', {
      updateQueryFilters: 'updateQueryFilters',
    }),

    handler(val) {
      if (this.isFirstLoading || val === undefined || val === null || val.length === 0 || val.length < 2) {
        this.autocompleteText = val;
        return false;
      }

      this.autocompleteText = val;

      this.ui.loading = true;

      return this.fetchResults();
    },

    fetchResults: _.debounce(function () { // eslint-disable-line
      this.cancellationTokens.forEach((cancellationToken) => {
        cancellationToken('cancelled-request');
      });

      const { CancelToken } = axios;

      const source = CancelToken.source();

      this.cancellationTokens.push(source.cancel);

      const params = {
        params: { s: this.autocompleteText, target: this.searchType },
        cancelToken: source.token,
      };

      axios.get(this.config.suggestionsUrl, params)
        .then((response) => {
          this.suggestions = [];
          this.selected = null;

          const { jobRoles = [], companyIndustries = [], companies = [] } = response.data.data;

          if (jobRoles.length) {
            this.suggestions.push({ name: 'jobRoles', data: jobRoles });
          }

          if (companyIndustries.length) {
            this.suggestions.push({ name: 'companyIndustries', data: companyIndustries });
          }

          if (companies.length) {
            this.suggestions.push({ name: 'companies', data: companies });
          }

          if (companyIndustries.length && companies.length) {
            // this.$ga({ category: 'search', label: 'suggestions', value: 'no-results' });
            this.$gtag.send({ event: 'suggestions', action: 'no-results', category: 'search' });
          }

          this.ui.loading = false;
        }).catch((error) => {
          if (error.message === 'cancelled-request') {
            console.log('request cancelled.');
          } else {
            console.log(error);
            this.$bus.$emit('flashNoticeError', {
              action: 'search request failed',
            });
          }
        });
    }, 500),

    submit({ text }) {
      this.ui.loading = false;

      this.updateQueryFilters({
        filterValues: text,
        filterName: 's',
      });
    },

    renderSuggestion(suggestion) {
      const { name } = _.cloneDeep(suggestion.item);
      const regExp = new RegExp(this.autocompleteText, 'ig');
      const matched = name.match(regExp);

      if (matched !== null) {
        return name.replace(regExp, `<strong>${matched[0]}</strong>`);
      }

      // We need to return the unformatted string if there aren't matches as
      // ES matches without punctuation and alternate name but this autocomplete doesn't
      // e.g. JP > J.P Morgan
      // e.g. Pricewaterhouse > PwC
      return name;
    },

    getSuggestionValue(suggestion) {
      const { item } = suggestion;
      return item.name;
    },

    resetText() {
      this.$refs.autocompleteInput.searchInput = '';
      this.autocompleteText = '';
      this.updateQueryFilters({
        filterValues: '',
        filterName: 's',
      });
    },
  },
};
</script>

<style type="text/scss" lang="scss">
@import "../../../../scss/abstracts/abstracts";

.ResetInput {
  position: absolute;
  top: 0;
  width: 40px;
  height: 40px;
  text-align: center;
  right: 50px;
  line-height: 40px;
}

.SearchInput {
  position: relative;

  .LoadingScreen {
    width: 30px;
    height: 30px;
    background: transparent;
    top: 5px;
    left: auto;
    right: 55px;
  }

  .LoadingScreen-spinner {
    position: relative;
    top: 0;
    transform: none;
    display: block;
    left: 0;
  }

  .Button {
    position: absolute;
    right: 1px;
    bottom: 1px;
    top: 1px;
    box-shadow: none;
    border-radius: 0 6px 6px 0;
    //color: color('grey', 'darkest');
    height: auto;
    display: flex;
    align-items: center;
    //background-color: transparent;

    &.is-active,
    &:focus, &:hover {
      transform: none;
      color: color('white');
      background-color: color('green');

      span {
        color: white;
      }
    }

    span {
      font-size: 20px;
      line-height: 1;
    }

  }
}

.avatar {
  height: 25px;
  width: 25px;
  border-radius: 20px;
  margin-right: 10px;
}
</style>
